/**
 * Reusable product photo component for Story 10.7-3.
 * Shows primary photo thumbnail, loading skeleton, or no-image placeholder.
 * Used in catalog grid (lg), staff quick sale lines (md), and cart line items (sm).
 *
 * Story 10.7-4 additions:
 * - sm/md sizes fetch thumbnail variant; lg fetches original
 * - One automatic retry after 3 s on fetch failure; shows retry indicator
 * - Optional eager prop to skip lazy loading for above-the-fold items
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PhotoIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { useAttachmentUrl } from "../../hooks/useAttachmentUrl";

const SIZE_CLASSES = {
  sm: "w-10 h-10",   // 40px — cart/basket line items
  md: "w-12 h-12",   // 48px — staff product selection
  lg: "w-full aspect-square", // full-width card thumbnail for catalog grid
};

interface ProductPhotoProps {
  attachmentId: string | null | undefined;
  size?: "sm" | "md" | "lg";
  className?: string;
  eager?: boolean;
}

export function ProductPhoto({
  attachmentId,
  size = "md",
  className = "",
  eager = false,
}: ProductPhotoProps) {
  const { t } = useTranslation("catalog");
  // sm/md use the smaller WebP thumbnail; lg uses original for higher resolution.
  const variant = size === "lg" ? "original" : "thumbnail";

  // Retry state: retryKey increments to force useAttachmentUrl's useEffect to re-run.
  const [retryCount, setRetryCount] = useState(0);
  const [retryKey, setRetryKey] = useState(0);
  const [showRetryIndicator, setShowRetryIndicator] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(eager);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (eager) {
      setShouldLoad(true);
      return;
    }
    setShouldLoad(false);
  }, [attachmentId, eager, size]);

  useEffect(() => {
    if (shouldLoad || eager || !attachmentId) return;
    if (!containerRef.current || typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setShouldLoad(true);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [attachmentId, eager, shouldLoad]);

  const requestedAttachmentId = shouldLoad ? (attachmentId ?? null) : null;
  const { url, loading } = useAttachmentUrl(requestedAttachmentId, variant, retryKey);

  // One automatic retry after 3 seconds when fetch fails (url null, not loading,
  // retryCount < 1). After second failure, stay on static placeholder.
  useEffect(() => {
    if (shouldLoad && url === null && !loading && attachmentId && retryCount < 1) {
      setShowRetryIndicator(true);
      const timer = setTimeout(() => {
        setRetryCount((c) => c + 1);
        setRetryKey((k) => k + 1);
        setShowRetryIndicator(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
    // When url is loaded successfully, reset so future failures can retry again.
    if (url !== null) {
      setRetryCount(0);
      setShowRetryIndicator(false);
    }
  }, [url, loading, attachmentId, retryCount]);

  const containerClass = `${SIZE_CLASSES[size]} ${className} flex-shrink-0 rounded overflow-hidden bg-neutral-100 dark:bg-neutral-700`;

  if (loading) {
    return (
      <div
        ref={containerRef}
        className={`${containerClass} animate-pulse`}
        data-testid="photo-skeleton"
        aria-label={t("photo_loading")}
      />
    );
  }

  if (url) {
    return (
      <div className={containerClass}>
        <img
          src={url}
          alt={t("photo_alt")}
          className="w-full h-full object-cover"
          loading={eager ? "eager" : "lazy"}
          data-testid="product-photo-img"
        />
      </div>
    );
  }

  // No photo (attachmentId null) or error fetching
  return (
    <div
      ref={containerRef}
      className={`${containerClass} flex flex-col items-center justify-center gap-0.5`}
      data-testid="photo-placeholder"
      aria-label={t("photo_no_image_aria")}
    >
      {showRetryIndicator ? (
        <ArrowPathIcon
          className="w-4 h-4 text-neutral-400 dark:text-neutral-500 animate-spin"
          data-testid="photo-retry-indicator"
          aria-label={t("photo_retrying")}
        />
      ) : (
        <>
          <PhotoIcon className="w-4 h-4 text-neutral-400 dark:text-neutral-500" />
          <span
            className={`text-neutral-400 dark:text-neutral-500 ${
              size === "lg" ? "text-xs" : "text-[10px]"
            }`}
          >
            {t("photo_no_image")}
          </span>
        </>
      )}
    </div>
  );
}
